'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Language, Translations, getTranslations, SUPPORTED_LANGUAGES } from '@/lib/i18n';

const LANG_KEY = 'muyu_lang_pref';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'EN',
  setLanguage: () => {},
  t: getTranslations('EN'),
  supportedLanguages: SUPPORTED_LANGUAGES,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('EN');
  const [t, setT] = useState<Translations>(getTranslations('EN'));

  useEffect(() => {
    const saved = localStorage.getItem(LANG_KEY) as Language | null;
    if (saved && SUPPORTED_LANGUAGES.find(l => l.code === saved)) {
      setLanguageState(saved);
      setT(getTranslations(saved));
      const lang = SUPPORTED_LANGUAGES.find(l => l.code === saved);
      if (lang) document.documentElement.setAttribute('dir', lang.dir);
    }
  }, []);

  // Listen for language changes dispatched from AppLayout or ProfileDashboard
  useEffect(() => {
    const handler = (e: Event) => {
      const code = (e as CustomEvent<Language>).detail;
      if (code && SUPPORTED_LANGUAGES.find(l => l.code === code)) {
        setLanguageState(code);
        setT(getTranslations(code));
        const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
        if (lang) document.documentElement.setAttribute('dir', lang.dir);
      }
    };
    window.addEventListener('language-change', handler);
    return () => window.removeEventListener('language-change', handler);
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    setT(getTranslations(lang));
    localStorage.setItem(LANG_KEY, lang);
    const langObj = SUPPORTED_LANGUAGES.find(l => l.code === lang);
    if (langObj) document.documentElement.setAttribute('dir', langObj.dir);
    window.dispatchEvent(new CustomEvent('language-change', { detail: lang }));
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, supportedLanguages: SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
